import { DocumentNode, useMutation } from '@apollo/client';
import {
  BottomNavigation,
  Button,
  Controller,
  FormDirtyModal,
  PageModal,
  TextEditor,
  Typography,
  useForm,
} from '@otp/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApproveUserMergeRequestDocument,
  RejectUserMergeRequestDocument,
} from 'src/graphql/generated/graphql';
import { UserMergeRequestListItem } from '../../list/merge-requests/types';

type Form = {
  responseNotes: string;
};

export enum ActionFormType {
  APPROVE = 'approve',
  REJECT = 'reject',
}

type ActionFormProps = {
  type: ActionFormType;
  entity: UserMergeRequestListItem;
  refetchQueries?: DocumentNode[];
  onCancel: () => void;
  onSave: () => void;
};

export const ActionForm = ({
  type,
  entity,
  refetchQueries = [],
  onCancel,
  onSave,
}: ActionFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'user-merge-requests.form',
  });

  const [displayCancelConfirmation, setDisplayCancelConfirmation] =
    useState(false);

  const [approveUserMergeRequest, { loading: loadingApprove }] = useMutation(
    ApproveUserMergeRequestDocument,
  );
  const [rejectUserMergeRequest, { loading: loadingReject }] = useMutation(
    RejectUserMergeRequestDocument,
  );
  const loading = loadingApprove || loadingReject;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<Form>({
    defaultValues: {
      responseNotes: entity.responseNotes || '',
    },
  });

  const onClose = () => {
    if (isDirty) {
      setDisplayCancelConfirmation(true);
    } else {
      onCancel();
    }
  };

  const onSubmit = async (data: Form) => {
    switch (type) {
      case ActionFormType.APPROVE:
        await approveUserMergeRequest({
          variables: { id: entity.id, data },
          refetchQueries,
        });
        break;
      case ActionFormType.REJECT:
        await rejectUserMergeRequest({
          variables: { id: entity.id, data },
          refetchQueries,
        });
        break;
    }

    onSave();
  };

  const title =
    type === ActionFormType.APPROVE ? t('title-approve') : t('title-reject');

  const description =
    type === ActionFormType.APPROVE
      ? t('description-approve')
      : t('description-reject');

  const buttonText =
    type === ActionFormType.APPROVE ? t('approve') : t('reject');

  const buttonColor = type === ActionFormType.APPROVE ? 'primary' : 'error';

  return (
    <PageModal open={true} onClose={onClose} title={title}>
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Typography className="mb-2" variant="subtitle1">
              {description}
            </Typography>
          </div>
          <div className="mb-4">
            <Controller
              name="responseNotes"
              control={control}
              rules={{ required: type === ActionFormType.REJECT }}
              render={({ field }) => (
                <div>
                  <Typography
                    className="mb-2"
                    variant="subtitle1"
                    color={!!errors.responseNotes ? 'red' : ''}
                  >
                    {t('fields.response-notes')}
                    {type === ActionFormType.REJECT && (
                      <span className="text-red-500"> *</span>
                    )}
                  </Typography>
                  <TextEditor {...field} defaultValue={field.value} />
                </div>
              )}
            />
          </div>
          <BottomNavigation>
            <Button variant="outlined" className="grow" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="grow"
              disabled={loading}
              color={buttonColor}
            >
              {buttonText}
            </Button>
          </BottomNavigation>
        </form>
      </div>
      <FormDirtyModal
        open={displayCancelConfirmation}
        onClose={() => setDisplayCancelConfirmation(false)}
        onCloseWithoutSaving={onCancel}
      />
    </PageModal>
  );
};
